<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Bitrix24PortalMembersAPI from 'dashboard/api/bitrix24PortalMembers';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const members = ref([]);
const loading = ref(false);
const syncing = ref(false);
const pending = ref({});
const searchQuery = ref('');
const lastSyncedAt = ref(null);
const { t } = useI18n();

const accessHint = member => {
  if (pending.value[member.bitrix_user_id])
    return t('AGENT_MGMT.ACCESS.SAVING');
  if (!member.bitrix_active) return t('AGENT_MGMT.ACCESS.INACTIVE');
  if (!member.email) return t('AGENT_MGMT.ACCESS.NO_EMAIL');
  return t(
    member.access_enabled
      ? 'AGENT_MGMT.ACCESS.REVOKE'
      : 'AGENT_MGMT.ACCESS.GRANT'
  );
};

const memberInitials = name =>
  (name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0])
    .join('') || '?';

const filteredMembers = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase('ru');
  if (!query) return members.value;
  return members.value.filter(member =>
    [member.name, member.email, member.phone]
      .filter(Boolean)
      .some(value => value.toLocaleLowerCase('ru').includes(query))
  );
});

const enabledCount = computed(
  () => members.value.filter(member => member.access_enabled).length
);

const formatSyncedAt = value =>
  value
    ? new Intl.DateTimeFormat('ru-RU', {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(value))
    : 'ещё не обновлялся';

const loadMembers = async () => {
  loading.value = true;
  try {
    const { data } = await Bitrix24PortalMembersAPI.get();
    members.value = data.members || [];
    lastSyncedAt.value =
      members.value
        .map(member => member.synced_at)
        .filter(Boolean)
        .sort()
        .at(-1) || null;
  } catch (error) {
    useAlert(
      error?.response?.data?.error || 'Не удалось загрузить список операторов.'
    );
  } finally {
    loading.value = false;
  }
};

const loadBitrixScript = () =>
  new Promise((resolve, reject) => {
    if (window.BX24) {
      resolve();
      return;
    }
    const existing = document.querySelector('script[data-medup-bitrix-sdk]');
    if (existing) {
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://api.bitrix24.com/api/v1/';
    script.dataset.medupBitrixSdk = 'true';
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });

const bitrixAuth = async () => {
  await loadBitrixScript();
  await new Promise(resolve => window.BX24.init(resolve));
  const auth = window.BX24.getAuth();
  if (!auth?.access_token || !auth?.domain || !auth?.member_id) {
    throw new Error(
      'Bitrix24 не передал данные авторизации. Обновите приложение.'
    );
  }
  return auth;
};

const syncDirectory = async ({ silent = false } = {}) => {
  syncing.value = true;
  try {
    const auth = await bitrixAuth();
    const { data } = await Bitrix24PortalMembersAPI.sync({
      access_token: auth.access_token,
      domain: auth.domain,
      member_id: auth.member_id,
    });
    members.value = data.members || [];
    lastSyncedAt.value = data.sync?.synced_at || new Date().toISOString();
    if (!silent)
      useAlert(
        `Список обновлён: ${data.sync?.total || members.value.length} сотрудников Bitrix24.`
      );
  } catch (error) {
    if (!silent) {
      useAlert(
        error?.response?.data?.error ||
          error.message ||
          'Не удалось обновить список из Bitrix24.'
      );
    }
  } finally {
    syncing.value = false;
  }
};

const updateMember = async (member, changes) => {
  if (pending.value[member.bitrix_user_id]) return;
  const previous = { access_enabled: member.access_enabled, role: member.role };
  pending.value[member.bitrix_user_id] = true;
  Object.assign(member, changes);
  try {
    const { data } = await Bitrix24PortalMembersAPI.update(
      member.bitrix_user_id,
      {
        access_enabled: member.access_enabled,
        role: member.role,
      }
    );
    Object.assign(member, data.member);
    useAlert(
      member.access_enabled
        ? 'Доступ к MedUp Chat выдан.'
        : 'Доступ к MedUp Chat отключён.'
    );
  } catch (error) {
    Object.assign(member, previous);
    useAlert(
      error?.response?.data?.error || 'Не удалось изменить права сотрудника.'
    );
  } finally {
    pending.value[member.bitrix_user_id] = false;
  }
};

const toggleAccess = member =>
  updateMember(member, { access_enabled: !member.access_enabled });

const updateRole = (member, role) => updateMember(member, { role });

onMounted(async () => {
  await loadMembers();
  // The roster stays cached for read-only access, then an administrator's
  // Bitrix OAuth context refreshes it without retaining a token server-side.
  syncDirectory({ silent: true });
});
</script>

<template>
  <SettingsLayout
    class="medup-operators min-w-0 !gap-5"
    :is-loading="loading"
    loading-message="Загружаем операторов…"
    :no-records-found="!members.length"
    no-records-message="Список ещё не загружен из Bitrix24."
  >
    <template #header>
      <header class="flex flex-wrap items-center justify-between gap-4">
        <div class="min-w-0">
          <h1 class="m-0 text-xl font-bold leading-tight text-n-slate-12">
            {{ $t('AGENT_MGMT.HEADER') }}
          </h1>
          <p class="mt-1.5 mb-0 text-[13px] leading-relaxed text-n-slate-11">
            Сотрудники Bitrix24. Переключатель определяет, может ли сотрудник
            войти в MedUp Chat.
          </p>
        </div>
        <div class="flex w-full flex-wrap items-center gap-3 xl:w-auto">
          <label
            class="flex h-9 w-full items-center gap-2 rounded-[10px] border border-n-weak bg-white px-3 text-n-slate-11 transition-colors focus-within:border-n-brand sm:w-[250px] dark:bg-n-surface-2"
          >
            <span
              class="i-lucide-search size-3.5 shrink-0"
              aria-hidden="true"
            />
            <input
              v-model="searchQuery"
              type="search"
              :aria-label="$t('AGENT_MGMT.SEARCH_PLACEHOLDER')"
              placeholder="Поиск по имени, e-mail или телефону"
              class="!m-0 !h-auto min-w-0 w-full !rounded-none !border-0 !bg-transparent !p-0 !text-[13px] text-n-slate-12 !shadow-none !outline-none focus:!shadow-none placeholder:text-n-slate-10"
            />
          </label>
          <Button
            label="Обновить из Bitrix24"
            size="sm"
            icon="i-lucide-refresh-cw"
            class="!h-9 !rounded-[10px] !px-4 !text-[13px] !font-semibold"
            :is-loading="syncing"
            @click="syncDirectory()"
          />
        </div>
      </header>
      <div
        class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-xs leading-relaxed text-n-slate-11"
      >
        <span class="font-medium">
          Доступ выдан: {{ enabledCount }} из {{ members.length }}
        </span>
        <p class="m-0">
          Последняя синхронизация: {{ formatSyncedAt(lastSyncedAt) }}. Права
          меняются только здесь; приглашения по e-mail не используются.
        </p>
      </div>
    </template>

    <template #body>
      <div
        class="w-full max-w-full overflow-auto overscroll-x-contain [scrollbar-gutter:stable] [scrollbar-width:thin] max-h-[max(280px,calc(100dvh-360px))]"
      >
        <table class="w-full min-w-[960px] border-collapse text-left">
          <caption class="sr-only">
            {{
              $t('AGENT_MGMT.HEADER')
            }}
          </caption>
          <thead
            class="sticky top-0 z-10 bg-[#f9f9fb] text-[11px] font-semibold uppercase tracking-[0.02em] text-n-slate-11 dark:bg-n-surface-2"
          >
            <tr
              class="[&>th]:border-b [&>th]:border-n-weak [&>th]:p-3 [&>th]:text-left [&>th]:font-semibold"
            >
              <th scope="col">{{ $t('AGENT_MGMT.LIST.NAME') }}</th>
              <th scope="col">{{ $t('SIDEBAR.CONTACTS') }}</th>
              <th scope="col">Роль</th>
              <th scope="col">{{ $t('AGENT_MGMT.LIST.STATUS') }}</th>
              <th scope="col">
                {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.LABEL') }}
              </th>
            </tr>
          </thead>
          <tbody class="text-sm text-n-slate-12">
            <tr
              v-for="member in filteredMembers"
              :key="member.bitrix_user_id"
              :aria-busy="Boolean(pending[member.bitrix_user_id])"
              class="transition-colors hover:bg-[#5929e2]/[0.025] dark:hover:bg-n-surface-2 [&>td]:border-b [&>td]:border-n-weak [&>td]:p-3 [&>td]:align-middle motion-reduce:transition-none"
            >
              <td class="min-w-[240px]">
                <div class="flex items-center gap-3">
                  <span
                    class="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#5929e2] text-[13px] font-semibold text-white"
                    aria-hidden="true"
                  >
                    {{ memberInitials(member.name) }}
                  </span>
                  <div class="flex min-w-0 flex-col gap-0.5">
                    <span class="text-sm font-medium leading-snug">{{
                      member.name || 'Без имени'
                    }}</span>
                    <code class="text-[10px] text-n-slate-11">
                      {{ `#${member.bitrix_user_id}` }}
                    </code>
                  </div>
                </div>
              </td>
              <td class="min-w-[200px] max-w-[280px]">
                <div class="flex flex-col gap-0.5 text-[13px] leading-snug">
                  <span class="break-words">{{
                    member.email || 'В Bitrix24 не указан e-mail'
                  }}</span>
                  <span v-if="member.phone" class="text-n-slate-11">{{
                    member.phone
                  }}</span>
                </div>
              </td>
              <td>
                <select
                  :value="member.role"
                  :aria-label="`${$t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.LABEL')}: ${member.name}`"
                  class="!mb-0 min-w-[160px] cursor-pointer !rounded-lg !text-[13px] disabled:cursor-not-allowed disabled:opacity-50"
                  :class="
                    member.role === 'administrator'
                      ? '!text-[#5929e2] !font-semibold'
                      : '!text-n-slate-12 !font-normal'
                  "
                  :disabled="
                    pending[member.bitrix_user_id] || !member.bitrix_active
                  "
                  @change="updateRole(member, $event.target.value)"
                >
                  <option value="agent">
                    {{ $t('AGENT_MGMT.AGENT_TYPES.AGENT') }}
                  </option>
                  <option value="administrator">
                    {{ $t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR') }}
                  </option>
                </select>
              </td>
              <td>
                <span
                  v-if="member.bitrix_active"
                  class="inline-flex whitespace-nowrap rounded-md bg-[#34c759]/10 px-2 py-1 text-[11px] font-bold text-[#248a3d] dark:text-n-teal-11"
                >
                  {{
                    $t(
                      'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.INDEX.CARD.ACTIVE'
                    )
                  }}
                </span>
                <span
                  v-else
                  class="inline-flex whitespace-nowrap rounded-md bg-[#ff3b30]/10 px-2 py-1 text-[11px] font-bold text-[#d70015] dark:text-n-ruby-11"
                >
                  Уволен в Bitrix24
                </span>
              </td>
              <td class="min-w-[160px]">
                <div class="flex items-center">
                  <button
                    type="button"
                    role="switch"
                    :aria-checked="Boolean(member.access_enabled)"
                    :aria-busy="Boolean(pending[member.bitrix_user_id])"
                    :aria-label="`${accessHint(member)}: ${member.name}`"
                    :title="accessHint(member)"
                    :disabled="
                      pending[member.bitrix_user_id] ||
                      !member.bitrix_active ||
                      !member.email
                    "
                    class="inline-flex h-8 min-w-[112px] shrink-0 cursor-pointer items-center justify-center gap-[7px] whitespace-nowrap rounded-[9px] border border-solid px-[9px] py-0 text-[11px] font-semibold leading-normal transition-[border-color,background-color,color,opacity] duration-[180ms] enabled:hover:border-[#b9a1ef] enabled:hover:bg-[#faf8ff] disabled:cursor-not-allowed disabled:opacity-[0.52] motion-reduce:transition-none"
                    :class="
                      member.access_enabled
                        ? 'border-[#5929e2]/[0.26] bg-[#5929e2]/[0.07] text-[#5929e2]'
                        : 'border-[#d9dde5] bg-white text-[#6e6e73]'
                    "
                    @click="toggleAccess(member)"
                  >
                    <span
                      class="relative h-4 w-7 shrink-0 rounded-full transition-colors duration-[180ms] motion-reduce:transition-none"
                      :class="
                        member.access_enabled ? 'bg-[#5929e2]' : 'bg-[#c7c7cc]'
                      "
                      aria-hidden="true"
                    >
                      <span
                        class="absolute left-0.5 top-0.5 size-3 rounded-full bg-white shadow-[0_1px_3px_rgba(15,1,54,0.26)] transition-transform duration-[180ms] motion-reduce:transition-none"
                        :class="{
                          'translate-x-3': member.access_enabled,
                          'animate-pulse motion-reduce:animate-none':
                            pending[member.bitrix_user_id],
                        }"
                      />
                    </span>
                    <span aria-live="polite">
                      {{
                        $t(
                          pending[member.bitrix_user_id]
                            ? 'AGENT_MGMT.ACCESS.SAVING'
                            : member.access_enabled
                              ? 'AGENT_MGMT.ACCESS.ENABLED'
                              : 'AGENT_MGMT.ACCESS.DISABLED'
                        )
                      }}
                    </span>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="!filteredMembers.length && searchQuery">
              <td
                colspan="5"
                class="p-10 text-center text-[13px] text-n-slate-11"
              >
                По вашему запросу сотрудников не найдено.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </SettingsLayout>
</template>
